// packages/chatbot/src/components/ToolCallBubble.tsx
import { Card } from '@dopomogai/ui';
import {
  Wrench,
  Navigation,
  Hash,
  Sliders,
  ToggleLeft,
  Edit3,
  Receipt,
  MailCheck,
  Type,
} from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import type { ToolCall } from '../types';
import { normalizeFrontendActionsArgs, type FrontendAction } from '../tools/frontendActions';

interface ToolCallBubbleProps {
  message: ToolCall;
  agentAvatar?: React.ReactNode | string;
}

function dedupeActions(actions: FrontendAction[]): FrontendAction[] {
  const seen = new Set<string>();
  const out: FrontendAction[] = [];
  for (const a of actions) {
    const key = JSON.stringify({ n: a.name, p: a.params });
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

export function ToolCallBubble({ message, agentAvatar }: ToolCallBubbleProps) {
  const renderAvatar = () => {
    if (typeof agentAvatar === 'string') {
      return <img src={agentAvatar} alt="Agent" className="w-8 h-8 rounded-lg" />;
    }
    return (
      agentAvatar || (
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500">
          <Wrench className="w-4 h-4 text-white" />
        </div>
      )
    );
  };

  const prettyIfFrontendActions = () => {
    if (message.tool_name !== 'frontend_actions') return null;
    const normalized = normalizeFrontendActionsArgs(message.arguments || {});
    const actions = dedupeActions(normalized.actions || []);

    const iconFor = (name: FrontendAction['name']) => {
      switch (name) {
        case 'navigate':
          return <Navigation className="w-4 h-4 text-sky-700" />;
        case 'open_section':
          return <Hash className="w-4 h-4 text-emerald-700" />;
        case 'select_preset':
          return <Sliders className="w-4 h-4 text-indigo-700" />;
        case 'toggle_feature':
          return <ToggleLeft className="w-4 h-4 text-amber-700" />;
        case 'set_copy':
          return <Edit3 className="w-4 h-4 text-purple-700" />;
        case 'generate_quote':
          return <Receipt className="w-4 h-4 text-rose-700" />;
        case 'submit_lead_email':
          return <MailCheck className="w-4 h-4 text-emerald-700" />;
        case 'fill_field':
          return <Type className="w-4 h-4 text-slate-700" />;
        default:
          return <Wrench className="w-4 h-4 text-slate-700" />;
      }
    };

    const labelFor = (a: FrontendAction) => {
      const name = a.name;
      if (name === 'navigate') return <>Navigate to <code>{(a as any).params.path}</code></>;
      if (name === 'open_section') return <>Open section <code>#{(a as any).params.id}</code></>;
      if (name === 'select_preset') return <>Select preset <code>{(a as any).params.id}</code></>;
      if (name === 'toggle_feature')
        return (
          <>
            Turn {(a as any).params.enabled ? 'on' : 'off'} <code>{(a as any).params.id}</code>
          </>
        );
      if (name === 'set_copy')
        return (
          <>
            Set copy <code>{(a as any).params.field}</code>
            {typeof (a as any).params.index === 'number' ? <>[{(a as any).params.index}]</> : null}
          </>
        );
      if (name === 'generate_quote') return <>Generate quote</>;
      if (name === 'submit_lead_email') return <>Submit lead email</>;
      if (name === 'fill_field')
        return (
          <>
            Fill <code>{(a as any).params.formId}.{(a as any).params.fieldId}</code>
          </>
        );
      return <>Run {name}</>;
    };

    const summary =
      actions
        .slice(0, 2)
        .map((a) => {
          if (a.name === 'navigate') return `navigate to ${(a as any).params.path}`;
          if (a.name === 'open_section') return `open #${(a as any).params.id}`;
          return a.name.replace('_', ' ');
        })
        .join(', ');

    return (
      <div className="text-sm">
        <p className="text-xs text-slate-600 mb-2">
          Planned steps{summary ? `: ${summary}` : ''}
        </p>
        <ul className="space-y-1">
          {actions.map((a, i) => (
            <li key={`${a.name}-${i}`} className="flex items-center gap-2">
              {iconFor(a.name)}
              <span>{labelFor(a)}</span>
            </li>
          ))}
        </ul>
        <details className="mt-2">
          <summary className="text-xs text-slate-500 cursor-pointer">View raw args</summary>
          <div className="mt-1">
            <SyntaxHighlighter
              language="json"
              style={a11yLight}
              customStyle={{
                margin: 0,
                padding: '0.5rem',
                borderRadius: '0.375rem',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                fontSize: '0.75rem',
              }}
              PreTag="div"
            >
              {JSON.stringify(message.arguments, null, 2)}
            </SyntaxHighlighter>
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="flex gap-3 w-full justify-start">
      {renderAvatar()}
      <div className="flex flex-col gap-1 max-w-[85%] items-start">
        <Card className="p-3 border-2 border-amber-400 bg-amber-50 shadow-sm cursor-default select-text">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-4 h-4 text-amber-800" />
            <span className="font-semibold text-sm text-amber-900">
              Tool Call: {message.tool_name}
            </span>
          </div>
          {message.tool_name === 'frontend_actions' ? (
            prettyIfFrontendActions()
          ) : (
            <div className="text-sm">
              <p className="text-xs text-slate-600 mb-1">Arguments:</p>
              <SyntaxHighlighter
                language="json"
                style={a11yLight}
                customStyle={{
                  margin: 0,
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  fontSize: '0.75rem',
                }}
                PreTag="div"
              >
                {JSON.stringify(message.arguments, null, 2)}
              </SyntaxHighlighter>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
